/**
 * App-level zoom for native (Capacitor iOS/iPadOS/Mac Catalyst).
 *
 * On the web, browsers provide Cmd+/Cmd-/Cmd+0 natively — we don't
 * intercept there. On Capacitor, neither mobile Safari's page zoom
 * nor macOS's built-in view-zoom menu items are wired up by default,
 * so the shortcuts do nothing. We provide a simple document-level
 * zoom that listens for Cmd+= / Cmd+- / Cmd+0 and scales the whole
 * document via the legacy-but-widely-supported `zoom` CSS property
 * (WebKit + Chromium ship it even though it's non-standard).
 *
 * `zoom` on `<html>` scales visual output including click-coordinate
 * math, so React Flow's canvas tracking inside the editor still
 * works — the zoom just makes the entire UI chrome + canvas bigger
 * together. If a user wants just-the-canvas-bigger, they use the
 * React Flow controls (bottom-left of the editor); if they want
 * everything bigger, they use these shortcuts.
 *
 * Preference persists in localStorage under `ac.app.zoom`. Same
 * scope on every page reload; survives sign-out.
 */

import { isNativePlatform } from '@/lib/nativePlatform'

const STORAGE_KEY = 'ac.app.zoom'
const MIN_ZOOM = 0.5
const MAX_ZOOM = 3.0
const STEP = 0.1

function readZoom(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return 1
    const n = parseFloat(raw)
    if (!Number.isFinite(n)) return 1
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, n))
  } catch {
    return 1
  }
}

function writeZoom(z: number) {
  try {
    if (z === 1) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, String(z))
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
}

function applyZoom(z: number) {
  // `zoom` on documentElement scales everything nested inside.
  // Prefer it over `transform: scale()` because zoom reflows layout
  // (so scrollbars / fixed elements / hit-testing all stay correct),
  // while scale only affects the visual layer.
  const el = document.documentElement as HTMLElement & {
    style: { zoom: string }
  }
  el.style.zoom = z === 1 ? '' : String(z)
}

/**
 * Install global Cmd+= / Cmd+- / Cmd+0 handlers. No-op on web (the
 * browser's built-in zoom is already bound to these shortcuts).
 * Safe to call multiple times — guarded by a module-level flag.
 */
let installed = false
export function initAppZoom(): void {
  if (installed) return
  installed = true
  if (!isNativePlatform()) return
  if (typeof window === 'undefined') return

  // Restore the user's last zoom on page load. Do this even before
  // attaching the listener so the very first paint lands at the
  // correct scale.
  applyZoom(readZoom())

  window.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      // macOS uses Cmd (metaKey). We also accept Ctrl for any
      // hypothetical keyboard that doesn't surface a Command modifier
      // (iPadOS external keyboards occasionally do).
      const isZoomModifier = e.metaKey || e.ctrlKey
      if (!isZoomModifier) return

      let next: number | null = null
      // `e.key` = '=' on unshifted '+' key; '+' when shifted. Some
      // keyboards report '_' for Shift+'-' — treat as zoom-out too.
      if (e.key === '=' || e.key === '+') {
        next = Math.min(MAX_ZOOM, roundStep(readZoom() + STEP))
      } else if (e.key === '-' || e.key === '_') {
        next = Math.max(MIN_ZOOM, roundStep(readZoom() - STEP))
      } else if (e.key === '0') {
        next = 1
      } else {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      applyZoom(next)
      writeZoom(next)
    },
    // Capture phase so we win against any in-app handler that might
    // call `preventDefault` on the event first (e.g. a text input).
    true,
  )
}

/**
 * Round a zoom level to the nearest STEP multiple so repeated
 * Cmd+=/Cmd+- presses don't accumulate floating-point drift. 0.1 +
 * 0.2 === 0.30000000000000004 in JS; this prevents users from
 * seeing 1.1000000000001 in localStorage after a few presses.
 */
function roundStep(n: number): number {
  return Math.round(n / STEP) * STEP
}
